import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridValidRowModel,
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { BoughtList } from '@/API';
import { useServices } from '@/components/ServiceContext';
import { applyDefaultFilterOperators } from '@/utils/filter.ts';
import { ServiceDataGrid } from '@/components/ServiceDataGrid';
import log from 'loglevel';
import { useMessageQueue } from '@erickson/components';
import { GraphQLQuery } from '@lambda-layer/types/filter.ts';
import { isUserInGroup } from '@/auth/EricksonAuthenticator.tsx';
import { UserContext } from '@/context/userContext.tsx';
import {
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';

export interface OptOutPageState<T> extends URLSearchParams {
  query?: GraphQLQuery<T>;
}

/**
 * BoughtListOptOutPage component
 *
 * This component renders a data grid for displaying and managing bought lists.
 * It supports server-side pagination, filtering, and sorting.
 */
const BoughtListOptOutPage: React.FC = () => {
  const userContext = useContext(UserContext);

  const messageQueue = useRef(useMessageQueue()).current;

  const { boughtListService } = useServices();

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [isDetailedView, setIsDetailedView] = useState<boolean>(true);

  /**
   * Handles the row edit stop event.
   * Prevents the default behavior when the reason for stopping the edit is 'rowFocusOut'.
   *
   * @param params - The parameters of the row edit stop event.
   * @param event - The event object.
   */
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    if (params.reason === 'rowFocusOut') {
      event.defaultMuiPrevented = true;
    }
  };

  /**
   * Handles the edit button click event.
   * Sets the row mode to 'Edit' for the specified row.
   *
   * @param id - The ID of the row to edit.
   * @returns A function that updates the row mode model.
   */
  const handleEditClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    },
    [rowModesModel]
  );

  /**
   * Handles the save button click event.
   * Sets the row mode to 'View' for the specified row.
   *
   * @param id - The ID of the row to save.
   * @returns A function that updates the row mode model.
   */
  const handleSaveClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    },
    [rowModesModel]
  );

  /**
   * Handles the cancel button click event.
   * Sets the row mode to 'View' and ignores any modifications made to the row.
   *
   * @param id - The ID of the row to cancel editing.
   * @returns A function that updates the row mode model.
   */
  const handleCancelClick = useCallback(
    (id: GridRowId) => () => {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });
    },
    [rowModesModel]
  );

  /**
   * Updates a row in the data grid.
   *
   * @param newRow - The new state of the row after an edit.
   * @returns The updated row.
   */
  const processRowUpdate = useCallback(
    async (newRow: GridRowModel<BoughtList>) => {
      // Create a copy of the new row
      const updatedRow = { ...newRow };

      try {
        // Call the secure optOut method - user tracking is handled automatically on the backend
        const result = await boughtListService.optOut(
          updatedRow.id,
          updatedRow.optOut || false
        );
        updatedRow.optOutDateTime = result.optOutDateTime;
        updatedRow.optOutUpdatedBy = result.optOutUpdatedBy;

        messageQueue.addMessage({
          text: 'Row updated successfully',
          severity: 'success',
        });
      } catch (error) {
        log.error('Failed to update row:', error);

        messageQueue.addMessage({
          text: 'Error updating the row',
          severity: 'error',
        });
      }

      return updatedRow;
    },
    [boughtListService, messageQueue]
  );

  const handleQueryUpdated = React.useCallback(
    (query: GraphQLQuery<GridValidRowModel>) => {
      log.debug(`Bought List Opt-Out starting query: ${JSON.stringify(query)}`);

      // Define the cutoff date: records expired within the last 180 days
      const cutoffDateISO = new Date(
        Date.now() - 180 * 24 * 60 * 60 * 1000
      ).toISOString();

      // Extract any existing conditions from the provided query
      const providedConditions = query.and || [];

      // Combine the date filter with the provided conditions.
      // The date filter will be the first condition in the AND clause.
      query.and = [
        { expirationDate: { gt: cutoffDateISO } },
        ...providedConditions,
      ];

      log.debug(`Bought List Opt-Out updated query: ${JSON.stringify(query)}`);
      return query;
    },
    []
  );

  /**
   * Defines the columns for the data grid.
   */
  const columns: GridColDef<BoughtList>[] = useMemo(
    () =>
      applyDefaultFilterOperators([
        {
          field: 'id',
          headerName: 'ID',
          minWidth: 100,
        },
        {
          field: 'site1',
          headerName: 'Community',
          minWidth: 100,
        },
        {
          field: 'listName',
          headerName: 'List Name',
          minWidth: 200,
        },
        {
          field: 'firstName',
          headerName: 'First name',
          minWidth: 150,
        },
        {
          field: 'lastName',
          headerName: 'Last name',
          minWidth: 150,
        },
        {
          field: 'saluation',
          headerName: 'Salutation',
          minWidth: 200,
        },
        {
          field: 'address',
          headerName: 'Address',
          minWidth: 300,
        },
        {
          field: 'city',
          headerName: 'City',
          minWidth: 150,
        },
        {
          field: 'state',
          headerName: 'State',
          minWidth: 100,
        },
        {
          field: 'zipcode',
          headerName: 'Zipcode',
          minWidth: 120,
        },
        {
          field: 'doNotMail',
          headerName: 'Do Not Mail',
          type: 'boolean',
          width: 100,
          valueGetter: val => val === '1',
        },
        {
          field: 'optOut',
          headerName: 'Opt Out',
          type: 'boolean',
          width: 100,
          editable: true,
        },
        {
          field: 'optOutDateTime',
          headerName: 'Opt Out Date',
          type: 'dateTime',
          valueGetter: value => {
            if (!value) {
              return value;
            }
            return new Date(value);
          },
          width: 185,
          editable: false,
        },
        {
          field: 'optOutUpdatedBy',
          headerName: 'Last Updated By',
          minWidth: 200,
          editable: false,
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          getActions: ({ id }) => {
            const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
            return isInEditMode
              ? [
                  <GridActionsCellItem
                    key="save"
                    icon={<SaveIcon />}
                    label="Save"
                    onClick={handleSaveClick(id)}
                  />,
                  <GridActionsCellItem
                    key="cancel"
                    icon={<CancelIcon />}
                    label="Cancel"
                    onClick={handleCancelClick(id)}
                    color="inherit"
                  />,
                ]
              : [
                  <GridActionsCellItem
                    key="edit"
                    // Disable the edit button if the user is not in the 'Admin' or 'OptOutManager' group
                    disabled={
                      !isUserInGroup(userContext.user, [
                        'Admin',
                        'OptOutManager',
                      ])
                    }
                    icon={<EditIcon />}
                    label="Edit"
                    onClick={handleEditClick(id)}
                    color="inherit"
                  />,
                ];
          },
        },
      ]),
    [
      rowModesModel,
      handleSaveClick,
      handleCancelClick,
      userContext.user,
      handleEditClick,
    ]
  );

  /**
   * Generate column visibility model based on the detailed view toggle
   */
  const columnVisibilityModel = useMemo(() => {
    const baseVisibility = {
      id: false,
    };

    if (!isDetailedView) {
      return {
        ...baseVisibility,
        firstName: false,
        lastName: false,
        optOutUpdatedBy: false,
      };
    }

    return baseVisibility;
  }, [isDetailedView]);

  /**
   * Create the toggle control component for the data grid toolbar
   */
  const toggleControl = useMemo(
    () => (
      <FormControlLabel
        control={
          <Switch
            checked={isDetailedView}
            onChange={() => setIsDetailedView(!isDetailedView)}
            color="primary"
            size="small"
          />
        }
        label={<Typography variant="body2">Detailed View</Typography>}
      />
    ),
    [isDetailedView]
  );

  return userContext.user ? (
    <ServiceDataGrid
      rowSelection={false}
      service={boughtListService}
      pageSize={10}
      columns={columns}
      editMode="row"
      rowModesModel={rowModesModel}
      onRowEditStop={handleRowEditStop}
      onRowModesModelChange={setRowModesModel}
      processRowUpdate={processRowUpdate}
      onQueryUpdated={handleQueryUpdated}
      quickSearchFields={['saluation', 'address', 'zipcode']}
      toolbarCustomComponent={toggleControl}
      columnVisibilityModel={columnVisibilityModel}
      initialState={{
        columns: {
          columnVisibilityModel: {
            id: false,
          },
        },
      }}
    />
  ) : (
    <CircularProgress />
  );
};

export default BoughtListOptOutPage;
